import React from "react";
import { BookOpen, ExternalLink, GraduationCap, Database, FileText, Search, Library } from "lucide-react";

const LIBRARIES = [
  {
    id: "scholar",
    name: "Google Scholar",
    url: "https://scholar.google.com",
    color: "#1E90FF",
    icon: GraduationCap,
    description: "World's largest academic search engine — papers, theses, books, conference abstracts.",
    tagline: "Free · Cross-discipline · Citation tracking",
  },
  {
    id: "proquest",
    name: "ProQuest",
    url: "https://www.proquest.com",
    color: "#39FF14",
    icon: Database,
    description: "Global dissertation, journal, and newspaper archive across STEM and humanities.",
    tagline: "Subscription · Premium dissertations",
  },
  {
    id: "yoktez",
    name: "YÖK Tez",
    url: "https://tez.yok.gov.tr",
    color: "#FFCC00",
    icon: FileText,
    description: "Türkiye Yükseköğretim Kurulu — national thesis archive (PhD & MSc).",
    tagline: "Türkiye · Open access · TR theses",
  },
  {
    id: "oatd",
    name: "OATD",
    url: "https://oatd.org",
    color: "#FF2DAA",
    icon: Search,
    description: "Open Access Theses & Dissertations — 7+ million records from 1100+ universities.",
    tagline: "Open access · Global",
  },
  {
    id: "ebsco",
    name: "EBSCO",
    url: "https://www.ebsco.com",
    color: "#9D4EDD",
    icon: Library,
    description: "Curated journal databases across science, engineering, and medicine.",
    tagline: "Subscription · Peer-reviewed",
  },
];

export default function LibraryView() {
  return (
    <section className="border-2 border-[#1E90FF] bg-black" data-testid="library-view">
      <header className="bg-[#1E90FF] text-black px-4 py-3 flex items-center gap-2">
        <BookOpen className="w-5 h-5"/>
        <span className="font-mono text-sm uppercase tracking-[0.22em] font-extrabold">
          KÜTÜPHANE · Academic Research Databases
        </span>
      </header>

      <div className="p-4 space-y-4">
        <p className="font-mono text-sm font-bold text-white leading-relaxed">
          Direct portals to the world's leading academic databases. Click any card to open the
          database in a new tab and begin your research instantly.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {LIBRARIES.map((lib) => {
            const Icon = lib.icon;
            return (
              <a key={lib.id} href={lib.url} target="_blank" rel="noreferrer"
                data-testid={`library-card-${lib.id}`}
                className="group block border-2 bg-black p-4 transition-all"
                style={{ borderColor: lib.color }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 22px ${lib.color}88, inset 0 0 14px ${lib.color}33`;
                }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="flex items-center justify-between">
                  <Icon className="w-7 h-7" style={{ color: lib.color }}/>
                  <ExternalLink className="w-4 h-4 text-white group-hover:text-[#1E90FF]"/>
                </div>
                <div className="mt-3 text-xl font-extrabold tracking-tight"
                  style={{ color: lib.color, textShadow: `0 0 8px ${lib.color}66` }}>
                  {lib.name}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider font-bold text-white">
                  {lib.tagline}
                </div>
                <div className="mt-3 font-mono text-[12px] leading-relaxed font-bold text-white">
                  {lib.description}
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-wider font-bold"
                  style={{ color: lib.color }}>
                  → Open {lib.name}
                </div>
              </a>
            );
          })}
        </div>

        {/* Quick search across Google Scholar */}
        <QuickScholarSearch/>
      </div>
    </section>
  );
}

function QuickScholarSearch() {
  const [q, setQ] = React.useState("thermal insulation building physics");
  const url = `https://scholar.google.com/scholar?q=${encodeURIComponent(q)}`;
  return (
    <div className="border-2 border-[#1E90FF] bg-black p-4 mt-2" data-testid="library-quick-search">
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-4 h-4 text-[#1E90FF]"/>
        <span className="font-mono text-[12px] uppercase tracking-[0.22em] font-extrabold text-white">
          Quick Search · Google Scholar
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search keywords (e.g. EPS halloysite fire retardant)…"
          className="flex-1 bg-black border-2 border-[#1F1F1F] text-white font-mono text-sm placeholder:text-white/40 px-3 py-2 focus:outline-none focus:border-[#1E90FF]"
          data-testid="library-search-input"
        />
        <a href={url} target="_blank" rel="noreferrer"
          data-testid="library-search-go"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E90FF] text-black font-mono text-sm uppercase tracking-wider font-extrabold hover:bg-[#39FF14] transition-colors">
          Search
          <ExternalLink className="w-3.5 h-3.5"/>
        </a>
      </div>
    </div>
  );
}
